import type { BarcodeItem } from '../barcode/BarcodeScannerTypes';
import type { ResultWrapper } from '../base';
import type { DocumentData } from '../document_scanner';
import { _ScanbotSDKInternalUiV2 } from '../index';
import type { DeepPartial } from '../utils/utils';
import { mapRTUUIResult } from '../utils/utils';

import type { BarcodeMappedData } from './barcode/BarcodeInfoMapping';
import type { BarcodeScannerScreenConfiguration } from './barcode/BarcodeScannerScreenConfiguration';
import { BarcodeScannerUiResult } from './barcode/BarcodeScannerUiResult';
import { MultipleScanningMode } from './barcode/MultipleScanningModeUseCase';
import { SingleScanningMode } from './barcode/SingleScanningModeUseCase';
import type { CreditCardScannerScreenConfiguration } from './creditcard/CreditCardScannerScreenConfiguration';
import { CreditCardScannerUiResult } from './creditcard/CreditCardScannerUIResult';
import type { CroppingConfiguration } from './document/CroppingConfiguration';
import type { DocumentScanningFlow } from './document/DocumentScanningFlow';
import type { MrzScannerScreenConfiguration } from './mrz/MRZScannerScreenConfiguration';
import { MrzScannerUiResult } from './mrz/MRZScannerUIResult';
import type { TextPatternScannerScreenConfiguration } from './textpattern/TextPatternScannerScreenConfiguration';
import type { TextPatternScannerUiResult } from './textpattern/TextPatternScannerUIResult';

/** @internal */
export interface _ScanbotSDKCapacitorInternalPluginUiV2 {
  startBarcodeScanner(config: BarcodeScannerScreenConfiguration): Promise<ResultWrapper<BarcodeScannerUiResult>>;
  startDocumentScannerV2(config: DocumentScanningFlow): Promise<ResultWrapper<DocumentData>>;
  startCroppingScreenV2(config: CroppingConfiguration): Promise<ResultWrapper<DocumentData>>;
  startMRZScannerV2(config: MrzScannerScreenConfiguration): Promise<ResultWrapper<MrzScannerUiResult>>;
  startTextPatternScannerV2(
    config: TextPatternScannerScreenConfiguration,
  ): Promise<ResultWrapper<TextPatternScannerUiResult>>;
  startCreditCardScannerV2(
    config: CreditCardScannerScreenConfiguration,
  ): Promise<ResultWrapper<CreditCardScannerUiResult>>;
  /**
   * Helper functions, not for public usage
   */
  // args param is not used, but if we set the callback as first param, deprecated warning is shown
  registerBarcodeItemMapperCallback(args: Record<string, never>, callback: (barcodeItem: BarcodeItem) => void): void;
  onBarcodeItemMapper(args: { barcodeItemUuid: string; barcodeMappedData?: BarcodeMappedData | null }): void;
}

/**
 * Opens the Ready-To-Use UI screen for scanning barcodes with the desired configuration.
 *
 * @param {BarcodeScannerConfiguration} config
 * @returns {Promise<ResultWrapper<BarcodeScannerResult>>}
 */
export function startBarcodeScanner(
  config: BarcodeScannerScreenConfiguration,
): Promise<ResultWrapper<BarcodeScannerUiResult>> {
  if (config.useCase instanceof SingleScanningMode || config.useCase instanceof MultipleScanningMode) {
    const barcodeItemMapperCallback = config.useCase.barcodeInfoMapping.barcodeItemMapper;

    if (barcodeItemMapperCallback !== null) {
      _ScanbotSDKInternalUiV2.registerBarcodeItemMapperCallback({}, (barcodeItem) => {
        const barcodeItemUuid = `${barcodeItem.text}${barcodeItem.upcEanExtension}_${barcodeItem.format}`;

        barcodeItemMapperCallback(
          barcodeItem,
          (barcodeMappedData: BarcodeMappedData) =>
            _ScanbotSDKInternalUiV2.onBarcodeItemMapper({
              barcodeItemUuid,
              barcodeMappedData,
            }),
          () => _ScanbotSDKInternalUiV2.onBarcodeItemMapper({ barcodeItemUuid }),
        );
      });

      // On iOS, the communication with the native part is throwing an error (startBarcodeScannerV2) because of the barcodeItemMapper
      // callback in the configuration class (not parsable), that's why we need to remove it before executing startBarcodeScannerV2 method
      config.useCase.barcodeInfoMapping.barcodeItemMapper = null;
    }
  }

  return _ScanbotSDKInternalUiV2
    .startBarcodeScanner(config)
    .then((result: ResultWrapper<DeepPartial<BarcodeScannerUiResult>>) =>
      mapRTUUIResult(result, BarcodeScannerUiResult),
    );
}

/**
 * Opens the Ready-To-Use UI Document Scanner screen with the desired configuration.
 */
export function startDocumentScanner(config: DocumentScanningFlow): Promise<ResultWrapper<DocumentData>> {
  return _ScanbotSDKInternalUiV2.startDocumentScannerV2(config);
}

/**
 * Opens the Ready-To-Use UI Cropping screen with the desired configuration.
 */
export function startCroppingScreen(config: CroppingConfiguration): Promise<ResultWrapper<DocumentData>> {
  return _ScanbotSDKInternalUiV2.startCroppingScreenV2(config);
}

/**
 * Opens the Ready-To-Use UI MRZ Scanner screen with the desired configuration.
 */
export async function startMRZScanner(
  config: MrzScannerScreenConfiguration,
): Promise<ResultWrapper<MrzScannerUiResult>> {
  return _ScanbotSDKInternalUiV2
    .startMRZScannerV2(config)
    .then((result: ResultWrapper<DeepPartial<MrzScannerUiResult>>) => mapRTUUIResult(result, MrzScannerUiResult));
}

/**
 * Opens the Ready-To-Use UI Text Pattern scanner with the desired configuration.
 */
export function startTextPatternScanner(
  config: TextPatternScannerScreenConfiguration,
): Promise<ResultWrapper<TextPatternScannerUiResult>> {
  return _ScanbotSDKInternalUiV2.startTextPatternScannerV2(config);
}

/**
 * Opens the Ready-To-Use UI Credit Card scanner with the desired configuration.
 */
export async function startCreditCardScanner(
  config: CreditCardScannerScreenConfiguration,
): Promise<ResultWrapper<CreditCardScannerUiResult>> {
  return _ScanbotSDKInternalUiV2
    .startCreditCardScannerV2(config)
    .then((result: ResultWrapper<DeepPartial<CreditCardScannerUiResult>>) =>
      mapRTUUIResult(result, CreditCardScannerUiResult),
    );
}

export * from './barcode/ArTrackingOverlayConfiguration';
export * from './barcode/BarcodeInfoMapping';
export * from './barcode/BarcodeItemMapper';
export * from './barcode/BarcodeScannerConfiguration';
export * from './barcode/BarcodeScannerScreenConfiguration';
export * from './barcode/BarcodeScannerUiResult';
export * from './barcode/BarcodeTextLocalization';
export * from './barcode/BarcodeUseCase';
export * from './barcode/FindAndPickScanningModeUseCase';
export * from './barcode/MultipleScanningModeUseCase';
export * from './barcode/SingleScanningModeUseCase';

export * from './common/ActionBarConfiguration';
export * from './common/CameraConfiguration';
export * from './common/CameraPermission';
export * from './common/Common';
export * from './common/NavigationBarConfiguration';
export * from './common/ScanCompletionOverlay';
export * from './common/ScanbotAlertDialog';
export * from './common/TopBarConfiguration';
export * from './common/UserGuidanceConfiguration';
export * from './common/ViewFinderConfiguration';

export * from './creditcard/CreditCardScannerIntroScreenConfiguration';
export * from './creditcard/CreditCardScannerScreenConfiguration';
export * from './creditcard/CreditCardScannerScreenTextLocalization';
export * from './creditcard/CreditCardScannerUIResult';
export * from './creditcard/CreditCardScannerUserGuidance';
export * from './creditcard/CreditCardScanningProgressConfiguration';

export * from './document/AcknowledgementScreenConfiguration';
export * from './document/CameraScreenConfiguration';
export * from './document/CroppingConfiguration';
export * from './document/CroppingResult';
export * from './document/CroppingScreenConfiguration';
export * from './document/CroppingTextLocalization';
export * from './document/DocumentScannerCameraConfiguration';
export * from './document/DocumentScannerGuidanceVisibility';
export * from './document/DocumentScannerOutputSettings';
export * from './document/DocumentScannerScreens';
export * from './document/DocumentScannerTextLocalization';
export * from './document/DocumentScannerUserGuidance';
export * from './document/DocumentScanningFlow';
export * from './document/IntroductionScreenConfiguration';
export * from './document/ReorderPagesScreenConfiguration';
export * from './document/ReviewScreenConfiguration';

export * from './mrz/MRZFinderLayoutPreset';
export * from './mrz/MRZScannerIntroScreenConfiguration';
export * from './mrz/MRZScannerScreenConfiguration';
export * from './mrz/MRZScannerScreenTextLocalization';
export * from './mrz/MRZScannerUIResult';

export * from './textpattern/TextPatternScannerIntroScreenConfiguration';
export * from './textpattern/TextPatternScannerScreenConfiguration';
export * from './textpattern/TextPatternScannerScreenTextLocalization';
export * from './textpattern/TextPatternScannerUIResult';
